import { useEffect, useMemo, useState } from 'react';
import { Loader2, Users, Filter } from 'lucide-react';
import { expenseCaptureApi } from '../../services/expenseCaptureApi';
import type { ExpenseCompany, ExpenseReceipt } from '../../types/expense';
import { StatusChip } from './StatusChip';
import { ReceiptImage } from './ReceiptImage';
import { formatMoney, formatDate } from './format';

interface Props {
    companies: ExpenseCompany[];
    onSelect: (r: ExpenseReceipt) => void;
}

/**
 * Country-manager view (desktop-leaning, responsive). Scoped read-only list of
 * receipts the caller manages, with thumbnails and person/status/month/company
 * filters. Full reconciliation is Phase 2.
 */
export function ExpenseManagerView({ companies, onSelect }: Props) {
    const [receipts, setReceipts] = useState<ExpenseReceipt[]>([]);
    const [scope, setScope] = useState<string | string[]>('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [companyCode, setCompanyCode] = useState('');
    const [status, setStatus] = useState('');
    const [person, setPerson] = useState('');
    const [month, setMonth] = useState('');

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        expenseCaptureApi.managerList({
            companyCode: companyCode || undefined,
            status: status || undefined,
            userEmail: person || undefined,
            month: month || undefined,
        })
            .then(res => { if (active) { setReceipts(res.receipts); setScope(res.scope); } })
            .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Could not load receipts.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [companyCode, status, person, month]);

    const people = useMemo(
        () => Array.from(new Set(receipts.map(r => r.UserEmail))).sort(),
        [receipts],
    );
    const scopedCompanies = useMemo(() => {
        if (scope === 'all') return companies;
        const set = new Set(scope as string[]);
        return companies.filter(c => set.has(c.companyCode));
    }, [companies, scope]);

    const total = receipts.reduce((acc, r) => acc + (r.GrossAmount ?? 0), 0);

    if (error) {
        return <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <Users className="h-4 w-4" />
                <span>
                    Country-manager view{scope !== 'all' && Array.isArray(scope) ? ` · ${scope.join(', ').toUpperCase()}` : ''}
                </span>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                <Filter className="ml-1 h-4 w-4 text-gray-400" />
                {scopedCompanies.length > 1 && (
                    <select value={companyCode} onChange={e => setCompanyCode(e.target.value)}
                        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm">
                        <option value="">All companies</option>
                        {scopedCompanies.map(c => <option key={c.companyCode} value={c.companyCode}>{c.companyName}</option>)}
                    </select>
                )}
                <select value={status} onChange={e => setStatus(e.target.value)}
                    className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm">
                    <option value="">All statuses</option>
                    <option value="Draft">Draft</option>
                    <option value="Submitted">Submitted</option>
                </select>
                <select value={person} onChange={e => setPerson(e.target.value)}
                    className="max-w-[200px] rounded-md border border-gray-200 bg-white px-2 py-1 text-sm">
                    <option value="">All people</option>
                    {people.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                    className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm" />
            </div>

            {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
            ) : receipts.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">No receipts match these filters.</div>
            ) : (
                <>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{receipts.length} receipt{receipts.length === 1 ? '' : 's'}</span>
                        <span className="font-medium text-gray-700">Total: {formatMoney(total, receipts[0]?.Currency || 'EUR')}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {receipts.map(r => (
                            <button
                                key={r.Id}
                                type="button"
                                onClick={() => onSelect(r)}
                                className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-2 text-left hover:border-gray-300"
                            >
                                <ReceiptImage receiptId={r.Id} className="h-16 w-16 flex-shrink-0 rounded-lg border border-gray-100" />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="truncate font-medium text-gray-900">{r.Vendor || r.CategoryLabel || 'Receipt'}</p>
                                        <span className="ml-auto flex-shrink-0 font-semibold text-gray-900">{formatMoney(r.GrossAmount, r.Currency)}</span>
                                    </div>
                                    <p className="truncate text-xs text-gray-500">{formatDate(r.ExpenseDate)} · {r.UserEmail}</p>
                                    <div className="mt-1"><StatusChip status={r.Status} /></div>
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
